export const EVENT_DATE_FILTERS = [
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "weekend", label: "This weekend" },
  { value: "week", label: "This week" },
  { value: "all", label: "All dates" },
];

function localDateFromValue(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return new Date(value.getTime());

  if (typeof value === "string") {
    const isoDate = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDate) {
      return new Date(
        Number(isoDate[1]),
        Number(isoDate[2]) - 1,
        Number(isoDate[3])
      );
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(value) {
  const date = localDateFromValue(value);
  if (!date || Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(value, days) {
  const date = new Date(value.getTime());
  date.setDate(date.getDate() + days);
  return date;
}

export function getEventDateRange(event = {}) {
  const start = startOfDay(event.startDate || event.date || event.eventDate);
  if (!start) return null;
  const end = startOfDay(event.endDate) || start;
  return end < start ? { start, end: start } : { start, end };
}

export function getDiscoveryDateRange(filter, now = new Date()) {
  const today = startOfDay(now);
  if (!today || filter === "all") return null;

  if (filter === "today") return { start: today, end: today };
  if (filter === "tomorrow") {
    const tomorrow = addDays(today, 1);
    return { start: tomorrow, end: tomorrow };
  }

  if (filter === "week") {
    const daysUntilSunday = (7 - today.getDay()) % 7;
    return { start: today, end: addDays(today, daysUntilSunday) };
  }

  if (filter === "weekend") {
    if (today.getDay() === 0) {
      return { start: addDays(today, -1), end: today };
    }
    const daysUntilSaturday = 6 - today.getDay();
    const saturday = addDays(today, daysUntilSaturday);
    return { start: saturday, end: addDays(saturday, 1) };
  }

  return null;
}

function eventIntersectsRange(event, range) {
  if (!range) return true;
  const eventRange = getEventDateRange(event);
  if (!eventRange) return false;
  return eventRange.start <= range.end && eventRange.end >= range.start;
}

function normalizedSearchValue(value) {
  return String(value || "").trim().toLocaleLowerCase("en-GB");
}

function eventSearchText(event) {
  return [
    event.title,
    event.description,
    event.category,
    event.venueName,
    event.location,
    event.address,
    event.postcode,
    event.organiserName,
    event.contact,
  ]
    .map(normalizedSearchValue)
    .filter(Boolean)
    .join(" ");
}

export function filterEventsForDiscovery(
  events,
  { search = "", dateFilter = "all", category = "all", now = new Date() } = {}
) {
  const searchTerm = normalizedSearchValue(search);
  const categoryTerm = normalizedSearchValue(category);
  const dateRange = getDiscoveryDateRange(dateFilter, now);

  return events.filter((event) => {
    if (searchTerm && !eventSearchText(event).includes(searchTerm)) return false;
    if (
      categoryTerm !== "all" &&
      normalizedSearchValue(event.category) !== categoryTerm
    ) {
      return false;
    }
    return eventIntersectsRange(event, dateRange);
  });
}

export function getAvailableEventDateFilters(events, now = new Date()) {
  return EVENT_DATE_FILTERS.map((filter) => ({
    ...filter,
    count:
      filter.value === "all"
        ? events.length
        : filterEventsForDiscovery(events, { dateFilter: filter.value, now }).length,
  })).filter(({ value, count }) => value === "all" || count > 0);
}

export function getAvailableEventCategories(events) {
  const categories = new Map();

  events.forEach((event) => {
    const label = String(event.category || "").trim();
    if (label) categories.set(normalizedSearchValue(label), label);
  });

  return Array.from(categories.values()).sort((a, b) => a.localeCompare(b, "en-GB"));
}
